/* -- File Input component ----------------------------------------- */
// The plain .file-input is CSS only. This module drives the .file-drop zone:
// the native <input type="file"> covers the card, so clicking and dropping
// are the browser's own; on top of that it highlights a drag, filters the
// files by accept / data-max-size / data-max-files (a DROP bypasses accept),
// appends to a multiple selection instead of replacing it, lists the files
// (image previews, Intl-formatted sizes, remove buttons) and exposes the
// named State API (AGENTS.md "State API").

// Shared preamble (AGENTS.md "State API"); the implementation lives in core.js —
// build.ts rewrites this import into a df$.shadcn.shared binding in dist/.
import { defussGlobals, defussQuery, componentState, bindComponent, textLocale } from '../../../shared/state-api.js';

const df$ = defussGlobals();
const dfDollar = defussQuery();

// VERIFIED: (verify's component types ratchet - tsc -p tsconfig.components.json) every type
// this file's API docs state - arguments, return values, event details - holds
// against its code: a wrong one is a new type error and fails the build.

/** What file-drop:rejected carries. */
interface FileDropRejectedDetail {
  /** the files that were refused */
  files: File[];
  /** the message the component shows for them */
  message: string;
}

/** default = empty; dragover = files hover the zone; selected = files
 * chosen; error = some files were rejected (config.message). */
const fileInputStates = ['default', 'dragover', 'selected', 'error'];

/** setState() configs per state (getState() reports the rejection message shown). */
export interface FileInputStateConfigs {
  /** No files - setting it clears the selection. */
  default: {};
  /** Files are dragged over the zone. */
  dragover: {};
  /** Files chosen. */
  selected: {
    /** placeholder entries for the list (a name, or name + size in bytes + MIME type) */
    files?: Array<string | { name: string; size?: number; type?: string }>;
  };
  /** Some files were refused. */
  error: {
    /** the message shown (getState() reports the live one) */
    message?: string;
  };
}

const inputOf = (el) => dfDollar(el).find<HTMLInputElement>('.file-drop-input').get(0);

/** Does a file match the input's accept list (".pdf", "image/*", "image/png")? */
function accepts(input, file) {
  const list = (input.accept || '').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
  if (!list.length) return true;
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return list.some((a) => (a.startsWith('.') ? name.endsWith(a) : a.endsWith('/*') ? type.startsWith(a.slice(0, -1)) : type === a));
}

const lang = (el) => textLocale(el);

/** 1536 → "1.5 kB" (Intl unit formatting, the locale's own). */
function formatSize(el, bytes) {
  const units = ['byte', 'kilobyte', 'megabyte', 'gigabyte'];
  let i = 0;
  let n = bytes;
  while (n >= 1000 && i < units.length - 1) {
    n /= 1000;
    i++;
  }
  return new Intl.NumberFormat(lang(el), { style: 'unit', unit: units[i], unitDisplay: 'short', maximumFractionDigits: i ? 1 : 0 }).format(n);
}

const ICON_X = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';

/** Rebuild input.files from a File[] (DataTransfer is the only way to set it). */
function setFiles(input, files) {
  const dt = new DataTransfer();
  for (const f of files) dt.items.add(f);
  input.files = dt.files;
}

function renderList(el) {
  const input = inputOf(el);
  const list = dfDollar(el).find('.file-drop-list').get(0);
  if (!list) return;
  (el._urls || []).forEach((u) => URL.revokeObjectURL(u));
  el._urls = [];
  // the list is rebuilt per change: emptied and refilled through df$
  dfDollar(list).empty().append(
    [...input.files].map((file, i) => {
      const li = document.createElement('li');
      li.className = 'file-drop-item';
      const thumb = document.createElement('span');
      thumb.className = 'file-drop-thumb';
      thumb.setAttribute('aria-hidden', 'true');
      if (file.type.startsWith('image/')) {
        const img = document.createElement('img');
        img.alt = '';
        img.src = URL.createObjectURL(file);
        el._urls.push(img.src);
        thumb.append(img);
      } else {
        thumb.textContent = (file.name.split('.').pop() || 'file').slice(0, 4);
      }
      const name = document.createElement('div');
      name.className = 'file-drop-name';
      name.textContent = file.name;
      const meta = document.createElement('span');
      meta.className = 'file-drop-meta';
      meta.textContent = formatSize(el, file.size);
      name.append(meta);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'file-drop-remove';
      dfDollar(remove).html(ICON_X);
      remove.setAttribute('aria-label', `Remove ${file.name}`);
      remove.addEventListener('click', () => {
        setFiles(input, [...input.files].filter((_, k) => k !== i));
        el._kept = [...input.files];
        apply(el, [], true);
        // tell form listeners - flagged, so our own change handler skips it
        el._removing = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        el._removing = false;
      });
      li.append(thumb, name, remove);
      return li;
    }),
  );
}

/** Show the rejections (or clear them) and settle the state name. */
function apply(el, rejected, quiet = false) {
  const input = inputOf(el);
  const err = dfDollar(el).find('.file-drop-error').get(0);
  const message = rejected.length ? `Not added: ${rejected.map((r) => `${r.file.name} (${r.why})`).join(', ')}` : '';
  if (err) {
    err.textContent = message;
    err.setAttribute('role', 'alert');
  }
  el.dataset.stateName = rejected.length ? 'error' : input.files.length ? 'selected' : 'default';
  renderList(el);
  // Fires when chosen or dropped files are refused (type, size, count) - the files and the message shown.
  if (!quiet && rejected.length) el.dispatchEvent(new CustomEvent<FileDropRejectedDetail>('file-drop:rejected', { bubbles: true, detail: { files: rejected.map((r) => r.file), message } }));
}

/** Merge the new pick into the kept files, filter, write input.files back. */
function onPick(el) {
  const input = inputOf(el);
  const incoming = [...input.files];
  const kept = input.multiple ? el._kept || [] : [];
  const maxSize = parseFloat(el.dataset.maxSize || 'Infinity');
  const maxFiles = input.multiple ? parseFloat(el.dataset.maxFiles || 'Infinity') : 1;
  const key = (f) => `${f.name}|${f.size}|${f.lastModified}`;
  const seen = new Set(kept.map(key));
  const out = [...kept];
  const rejected = [];
  for (const file of incoming) {
    if (seen.has(key(file))) continue;
    if (!accepts(input, file)) rejected.push({ file, why: 'type' });
    else if (file.size > maxSize) rejected.push({ file, why: `over ${formatSize(el, maxSize)}` });
    else if (out.length >= maxFiles) rejected.push({ file, why: `max ${maxFiles}` });
    else {
      out.push(file);
      seen.add(key(file));
    }
  }
  setFiles(input, out);
  el._kept = out;
  apply(el, rejected);
}

/**
 * The markup of a state, for render(), on a detached copy of the authored
 * markup: the error line - the message in 'error', empty otherwise - as
 * apply() / the error state write it. The file list is built from the
 * input's FileList (blob-URL thumbnails, sizes): runtime-owned.
 */
function applyMarkup(el, stateName, config) {
  const err = dfDollar(el).find('.file-drop-error').first();
  if (!err.get(0)) return;
  err.attr('role', 'alert');
  if (stateName === 'error') err.text(config?.message || 'Not added: archive.zip (type)');
  else if (stateName !== 'dragover') err.text('');
}

function triggerStateChange(el, stateName, config) {
  const input = inputOf(el);
  switch (stateName) {
    case 'default':
      setFiles(input, []);
      el._kept = [];
      apply(el, [], true);
      break;
    case 'dragover':
      el.dataset.stateName = 'dragover';
      break;
    case 'selected': {
      // config.files: [{ name, size, type }] - placeholder files (demos, tests)
      // { files: [{ name, size, type }] } - or getState()'s plain names
      const files = (config?.files || [{ name: 'report.pdf', size: 248000, type: 'application/pdf' }]).map((f) => (typeof f === 'string' ? { name: f } : f)).map(
        // real bytes (capped at 5 MB) so size, type and name read true
        (f) => new File([new Uint8Array(Math.min(f.size ?? 0, 5e6))], f.name, { type: f.type || '' }),
      );
      setFiles(input, files);
      el._kept = [...input.files];
      apply(el, [], true);
      break;
    }
    case 'error': {
      const err = dfDollar(el).find('.file-drop-error').get(0);
      if (err) err.textContent = config?.message || 'Not added: archive.zip (type)';
      el.dataset.stateName = 'error';
      break;
    }
  }
}

/** Registry-level API; pass the .file-drop explicitly. Unknown names throw. */
export const fileInputApi = componentState({
  component: 'file-input',
  states: fileInputStates,
  apply: (el, state) => triggerStateChange(el, state.name, state.config),
  read: (el, state) => {
    const input = inputOf(el);
    return {
      name: el.dataset.stateName || 'default',
      config: {
        ...state.config,
        count: input.files.length,
        files: [...input.files].map((f) => f.name),
        // the live rejection message, so setState('error', getState().config) keeps it
        ...(el.dataset.stateName === 'error' ? { message: dfDollar(el).find('.file-drop-error').text() } : {}),
      },
    };
  },
  markup: (el, state) => applyMarkup(el, state.name, state.config),
});

df$.fileInputApi = fileInputApi;
df$.fileInputStates = fileInputStates;

function init() {
  dfDollar('.file-drop:not([data-init])').toArray().forEach((el) => {
    const input = inputOf(el);
    if (!input) return;
    el.dataset.init = '';
    el.dataset.stateName = 'default';
    el._kept = [];
    // the error line is a live region from the start (not only once a
    // rejection has been written)
    dfDollar(el).find('.file-drop-error').attr('role', 'alert');
    // el.store + el.api (AGENTS.md "State through stores")
    bindComponent(el, fileInputApi);
    const zone = dfDollar(el).find('.file-drop-zone').get(0) || el;
    let depth = 0; // dragenter/leave fire for every child - count them
    zone.addEventListener('dragenter', (e) => {
      if (!e.dataTransfer?.types.includes('Files') || input.disabled) return;
      depth++;
      el._before = el.dataset.stateName === 'dragover' ? el._before : el.dataset.stateName;
      el.dataset.stateName = 'dragover';
    });
    zone.addEventListener('dragleave', () => {
      depth = Math.max(0, depth - 1);
      if (!depth && el.dataset.stateName === 'dragover') el.dataset.stateName = el._before || 'default';
    });
    zone.addEventListener('drop', () => {
      depth = 0;
      if (el.dataset.stateName === 'dragover') el.dataset.stateName = el._before || 'default';
    });
    input.addEventListener('change', () => {
      // our own re-dispatch after a remove carries no new pick
      if (!el._removing) onPick(el);
    });
  });
}

init();
new MutationObserver(init).observe(document, { childList: true, subtree: true });
